import React from 'react';
import 'regenerator-runtime/runtime';
import { createRoot } from 'react-dom/client';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';

import PosApp from './App';
import { getNamespace } from './utils/helpers';
import { OpenTelemetryGate } from './utils/opentelemetry';

const DEV_PORT = '3021';

const isDevelopment = location.host.includes(DEV_PORT);
const isOutdatedBrowser = !window.hasOwnProperty('fetch');

const container = document.getElementById('root');
const root = createRoot(container);

const renderDom = async (configData) => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: configData.launchDarkly.clientSideID,
    options: {
      streaming: true,
      privateAttributes: ['email'],
    },
    context: {
      kind: 'multi',
      environment: {
        key: 'environment',
        environment: configData.environment || 'local',
        // Keep null when getNamespace has no segment (localhost / unmatched host).
        // LD treats null/absent attributes differently from '' (empty string can
        // match targeting rules); do not normalize null → '' here.
        namespace: getNamespace(window.location.hostname),
        hostname: window.location.hostname,
        // Hardcoded app-state/Helm chart name for this repo, matching the
        // `app` attribute cb_pos sets on its equivalent "environment"
        // context (see config/initializers/launch_darkly_client.rb) so LD
        // targeting rules built against that shape apply consistently here.
        app: 'pos-frontend',
      },
    },
    reactOptions: {
      useCamelCaseFlagKeys: false,
    },
    timeout: 5,
  });

  root.render(
    <LDProvider>
      <OpenTelemetryGate>
        <PosApp configData={configData} />
      </OpenTelemetryGate>
    </LDProvider>,
  );
};

const localHostStartup = () => {
  const configData = require('/src/static/config.json');
  renderDom(configData);
};

const k8sStartup = () => {
  try {
    fetch('/config.json')
      .then((response) => response.json())
      .then((configData) => renderDom(configData));
  } catch (error) {
    console.error('Config was not fetched, cannot render app.');
    console.log(`Error: ${error}`);
  }
};

if (isOutdatedBrowser) {
  // POS stations should all be running the app in same Chromium browser using a boot script.
  // If a POS cashier is seeing this message, they have booted their machine
  // incorrectly, and should reboot with the correct process.
  root.render(
    <div>
      <p>
        Your Cashiering station is running a browser than cannot support the
        Cashiering application.
      </p>
      <p>
        Please reboot your Cashiering station and consult a manager or IT
        support if the problem persists.
      </p>
    </div>,
  );
} else if (isDevelopment) {
  localHostStartup();
} else {
  k8sStartup();
}
